import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import {
    REGULAR_MODULE_TYPE,
    BANNER_MODULE_TYPE,
    BANNER_TILES_MODULE_TYPE,
    LANDING_MODULE_TYPE,
    STORYTELLING_MODULE_TYPE,
} from '../../constants/contentModuleConstants.js';
import { pageTypeConstants } from '../../constants/pageTypeConstants.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

import { SbRespContentModuleRegular } from '../SbRespContentModuleRegular/SbRespContentModuleRegular.tsx';
import { SbSharedContentModuleBanner } from '../SbSharedContentModuleBanner/SbSharedContentModuleBanner.tsx';
import { SbSharedContentModuleBannerTiles } from '../SbSharedContentModuleBannerTiles/SbSharedContentModuleBannerTiles.tsx';
import { SbRespContentModuleLanding } from '../SbRespContentModuleLanding/SbRespContentModuleLanding.tsx';
import { SbSharedContentModuleStorytellingHeader } from '../SbSharedContentModuleStorytelling/SbSharedContentModuleStorytellingHeader.tsx';

import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers.ts';

import { type SbRespContentModuleSelector_itemSearch$key } from './__generated__/SbRespContentModuleSelector_itemSearch.graphql.ts';
import { type SbRespContentModuleSelector_user$key } from './__generated__/SbRespContentModuleSelector_user.graphql.ts';
import { type SbRespContentModuleSelector_viewer$key } from './__generated__/SbRespContentModuleSelector_viewer.graphql.ts';

const itemFragment = graphql`
    fragment SbRespContentModuleSelector_itemSearch on ItemSearchQueryConnection {
        pageType
        seller {
            ...sellerBrandingHelpers_seller
        }
        browseContentModule(contentModuleId: $contentModuleId) {
            moduleType
            ...SbRespContentModuleRegular_browseContentModule
            ...SbSharedContentModuleBanner_browseContentModule
            ...SbSharedContentModuleIconicDesign_browseContentModule
            ...SbSharedContentModuleBannerTiles_browseContentModule
            ...SbRespContentModuleLanding_browseContentModule
            ...SbSharedContentModuleStorytellingHeader_browseContentModule
        }
    }
`;

const userFragment = graphql`
    fragment SbRespContentModuleSelector_user on User {
        ...SbRespContentModuleRegular_user
        ...sellerBrandingHelpers_user
    }
`;

const viewerFragment = graphql`
    fragment SbRespContentModuleSelector_viewer on Viewer {
        ...SbRespContentModuleLanding_viewer
    }
`;

type Props = {
    itemSearch: SbRespContentModuleSelector_itemSearch$key;
    user: SbRespContentModuleSelector_user$key | null | undefined;
    viewer: SbRespContentModuleSelector_viewer$key | null | undefined;
    className?: string;
    hidePhoto?: boolean;
};

export const SbRespContentModuleSelector: FC<Props> = ({
    itemSearch: itemSearchRef,
    user: userRef,
    viewer: viewerRef,
    className,
    hidePhoto = false,
}) => {
    const isClient = useSbSelector(state => state.relayVariables.variables.isClient);
    const itemSearch = useFragment(itemFragment, itemSearchRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const { browseContentModule, pageType, seller } = itemSearch || {};
    const isSalePage = pageType === pageTypeConstants.SALE;
    const isDealerShopPage = pageType === pageTypeConstants.DEALER;
    const useH1Header = pageType === pageTypeConstants.COLLECTION;
    if (isDealerShopPage) {
        if (!isClient) {
            return null;
        }

        const shouldShowSellerDescription =
            checkRewardedSeller(seller) || checkTradeUserUnderTopLevelLoyaltyTiers(user || null);
        if (!shouldShowSellerDescription) {
            return null;
        }
    }
    if (!browseContentModule) {
        return null;
    }

    switch (browseContentModule.moduleType) {
        case REGULAR_MODULE_TYPE:
            return (
                <SbRespContentModuleRegular
                    browseContentModule={browseContentModule}
                    hidePhoto={hidePhoto}
                    className={className}
                    user={user}
                />
            );
        case BANNER_MODULE_TYPE:
            return (
                <SbSharedContentModuleBanner
                    browseContentModule={browseContentModule}
                    renderImage={({ src, imgClassName, alt }) => (
                        <img src={src} className={imgClassName || ''} alt={alt || ''} />
                    )}
                />
            );
        case BANNER_TILES_MODULE_TYPE:
            return (
                <SbSharedContentModuleBannerTiles
                    browseContentModule={browseContentModule}
                    isSalePage={isSalePage}
                    useH1Header={useH1Header}
                />
            );
        case LANDING_MODULE_TYPE:
            return (
                <SbRespContentModuleLanding
                    browseContentModule={browseContentModule}
                    hidePhoto={hidePhoto}
                    className={className}
                    viewer={viewer}
                />
            );
        case STORYTELLING_MODULE_TYPE:
            return (
                <SbSharedContentModuleStorytellingHeader
                    browseContentModule={browseContentModule}
                />
            );
        default:
            return null;
    }
};
